'use client'

import { useTranslations } from 'next-intl'
import { Clock, Printer, Share2, MoreVertical, ChevronDown, Trash2, Ban, Settings, Check, Truck } from 'lucide-react'
import MockupChrome from './MockupChrome'
import '../../sass/landing/DeliveryPanelMockup.scss'

type AccentTone = 'yellow' | 'blue'

type OrderKey = 'o1' | 'o2' | 'o3'

interface OrderItem {
  qty: number
  itemKey: string
  done: boolean
}

interface Order {
  orderKey: OrderKey
  accent: AccentTone
  statusKey: 'ready' | 'preparing'
  items: OrderItem[]
}

const orders: Order[] = [
  {
    orderKey: 'o1',
    accent: 'yellow',
    statusKey: 'ready',
    items: [
      { qty: 2, itemKey: 'croquetasCaseras', done: true },
      { qty: 1, itemKey: 'cerveza', done: true },
      { qty: 1, itemKey: 'patatasBravas', done: true }
    ]
  },
  {
    orderKey: 'o2',
    accent: 'blue',
    statusKey: 'preparing',
    items: [
      { qty: 1, itemKey: 'tortillaEspanola', done: false },
      { qty: 1, itemKey: 'bocadillo', done: false },
      { qty: 2, itemKey: 'cocaCola', done: false }
    ]
  },
  {
    orderKey: 'o3',
    accent: 'blue',
    statusKey: 'preparing',
    items: [
      { qty: 2, itemKey: 'cafeConLeche', done: false },
      { qty: 1, itemKey: 'tostada', done: false }
    ]
  }
]

const DeliveryPanelMockup = () => {
  const t = useTranslations('LandingV2.mockups.delivery')

  return (
    <div className='delivery-panel' aria-label={t('ariaLabel')}>
      <MockupChrome
        icon={<Truck size={11} strokeWidth={2.4} />}
        address={t('address')}
      />

      <div className='delivery-panel__toolbar'>
        <div className='delivery-panel__brand'>
          <span className='delivery-panel__brand-title'>{t('brand')}</span>
          <div className='delivery-panel__brand-actions'>
            <button type='button' className='delivery-panel__icon-btn' tabIndex={-1} aria-label={t('historyAria')}>
              <Clock size={14} strokeWidth={2} />
            </button>
            <button type='button' className='delivery-panel__icon-btn' tabIndex={-1} aria-label={t('trashAria')}>
              <Trash2 size={14} strokeWidth={2} />
            </button>
            <button type='button' className='delivery-panel__icon-btn' tabIndex={-1} aria-label={t('blockAria')}>
              <Ban size={14} strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className='delivery-panel__status'>
          <span className='delivery-panel__status-dot' aria-hidden='true' />
          <span className='delivery-panel__status-text'>{t('statusReceiving')}</span>
        </div>

        <button type='button' className='delivery-panel__icon-btn delivery-panel__icon-btn--gear' tabIndex={-1} aria-label={t('settingsAria')}>
          <Settings size={14} strokeWidth={2} />
        </button>
      </div>

      <div className='delivery-panel__orders'>
        {orders.map((order) => (
          <article
            key={order.orderKey}
            className={`delivery-panel__order delivery-panel__order--${order.accent}`}
          >
            <span className='delivery-panel__order-accent' aria-hidden='true' />
            <header className='delivery-panel__order-head'>
              <p className='delivery-panel__order-customer'>
                {t(`orders.${order.orderKey}.customer`)} - {t(`orders.${order.orderKey}.phone`)}
              </p>
              <p className='delivery-panel__order-line'>{t(`orders.${order.orderKey}.address`)}</p>
              <p className='delivery-panel__order-line'>
                {t(`orders.${order.orderKey}.city`)} {t(`orders.${order.orderKey}.postal`)}
              </p>
              <p className='delivery-panel__order-date'>{t(`orders.${order.orderKey}.date`)}</p>
              <div className='delivery-panel__order-status'>
                <span>{t(`status.${order.statusKey}`)}</span>
                <ChevronDown size={12} strokeWidth={2} />
              </div>
            </header>
            <div className='delivery-panel__order-divider' aria-hidden='true' />
            <ul className='delivery-panel__order-items'>
              {order.items.map((item) => (
                <li key={item.itemKey} className='delivery-panel__order-item'>
                  <span className='delivery-panel__order-item-text'>
                    {item.qty} · {t(`orders.${order.orderKey}.items.${item.itemKey}`)}
                  </span>
                  {item.done
                    ? <Check size={14} strokeWidth={2.5} className='delivery-panel__order-item-icon delivery-panel__order-item-icon--done' />
                    : <Clock size={14} strokeWidth={2.5} className='delivery-panel__order-item-icon delivery-panel__order-item-icon--pending' />}
                </li>
              ))}
            </ul>
            <div className='delivery-panel__order-divider' aria-hidden='true' />
            <footer className='delivery-panel__order-foot'>
              <button type='button' className='delivery-panel__order-action' tabIndex={-1} aria-label={t('printAria')}>
                <Printer size={14} strokeWidth={2} />
              </button>
              <button type='button' className='delivery-panel__order-action' tabIndex={-1} aria-label={t('shareAria')}>
                <Share2 size={14} strokeWidth={2} />
              </button>
              <button type='button' className='delivery-panel__order-action' tabIndex={-1} aria-label={t('moreAria')}>
                <MoreVertical size={14} strokeWidth={2} />
              </button>
            </footer>
          </article>
        ))}
      </div>
    </div>
  )
}

export default DeliveryPanelMockup
